/*
 *  Copyright
 *
 *  Tegona S.A.
 *
 *  Tegona JS © 2017, Tegona S.A.
 *
 *  ALL RIGHTS RESERVED. THIS PROGRAM CONTAINS MATERIAL PROTECTED
 *  UNDER INTERNATIONAL AND SWITZERLAND COPYRIGHT LAWS AND TREATIES.
 *  ANY UNAUTHORIZED USE OF THE PROGRAM, INCLUDING REPRODUCTION,
 *  MODIFICATION, TRANSFER, TRANSMITTAL OR REPUBLICATION OF THIS
 *  MATERIAL IN ANY FORM OR BY ANY MEANS IS PROHIBITED WITHOUT
 *  SPECIFIC WRITTEN PERMISSION OF THE COPYRIGHT HOLDER.
 *
 *  copyright@tegona.com
 */

import React from 'react'
import autoBind from 'react-autobind'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import { injectIntl } from 'react-intl'
import { RaisedButton, FontIcon, IconButton } from 'material-ui'
import Cookies from 'cookies-js'
import moment from 'moment'

import { Face } from '@material-ui/icons'
import { List, ListItem, ListItemAvatar, Avatar, ListItemText, Typography } from '@material-ui/core'
import { CircularProgress } from 'material-ui'
import ReactMarkdown from 'react-markdown'

import { TextField } from 'generic/form'
import { FixedPanel, Panel } from 'generic/panels'
import Card from 'generic/card/Card'
import Base from 'components/common/Base'
import * as actions from 'actions/Chatbot'
import { messages, role, key } from 'constants/Chatbot'
import * as prestationActions from 'actions/ApplicantPrestation'
import * as applicantActions from 'actions/Applicant'
import * as headerConstants from 'constants/Header'

const optionalFields = ['applicant', 'beneficiary_name', 'beneficiary_number', 'beneficiary_type', 'interpreter', 'preferred_sex', 'preferred_nationality_origin', 'preferred_qualification', 'preferred_skill']

class Chatbot extends React.Component {

  constructor(props) {
    super(props)
    autoBind(this)

    this.state = {
      message: '',
      history: [{role: role.ASSISTANT, content: this.props.intl.formatMessage(messages.CHAT_PLACEHOLDER)}],
      loading: false,
      loadingDialog: false,
      stream: undefined,
      toolCall: null,
      invalidFields: [],
      confirmingPrestation: false,
      prestationTemplate: null,
      isPrestationValid: false,
      isPrestationCompleted: false,
    }

    this.fieldRef = React.createRef()
    this.historyRef = React.createRef()
  }

  componentDidMount() {
    prestationActions.loadPrestation(undefined)
      .then((result) => {
        this.setState({
          prestationTemplate: result
        })
      })
      .catch((error) => {
        console.error('Error loading prestation template:', error)
      })
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.stream !== this.state.stream) {
      const el = this.historyRef.current
      if (!el) return

      const isNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 100

      if (isNearBottom) el.scrollTop = el.scrollHeight
    }
  }

  handleRestartChatbot() {
    this.setState({
      message: '',
      history: [{role: role.ASSISTANT, content: this.props.intl.formatMessage(messages.CHAT_PLACEHOLDER)}],
      loading: false,
      loadingDialog: false,
      stream: undefined,
      toolCall: null,
      invalidFields: [],
      confirmingPrestation: false,
      prestationTemplate: null,
      isPrestationValid: false,
      isPrestationCompleted: false,
    })
    prestationActions.loadPrestation(undefined)
      .then((result) => {
        this.setState({
          prestationTemplate: result
        })
      })
      .catch((error) => {
        console.error('Error loading prestation template:', error)
      })
  }

  async handleOnClick() {
    if (!this.state.message || this.state.loading) return

    const messages = [
      ...this.state.history,
      {'role': 'user', 'content': this.state.message}
    ]

    this.setState({
      message: '',
      stream: '',
      loading: true,
      loadingDialog: true,
      history: messages,
    }, () => {
      if (this.fieldRef.current) this.fieldRef.current.focus()
      if (this.historyRef.current) this.historyRef.current.scrollTop = this.historyRef.current.scrollHeight
    })

    const response = await actions.postTest(messages, this.props.user && this.props.user.uid, event => {
      switch (event.type) {
        case "metadata":
          const suggestion = event.suggested_prestation
          // When there is no new suggestion (no tool call this turn), keep the
          // previous one displayed instead of clearing it.
          this.setState(prev => ({
            toolCall: suggestion ? suggestion.data : prev.toolCall,
            invalidFields: suggestion ? suggestion.invalid_fields : prev.invalidFields,
            history: event.messages || [],
            loadingDialog: false,
            isPrestationValid: suggestion ? suggestion.is_valid : prev.isPrestationValid,
            isPrestationCompleted: suggestion ? suggestion.is_valid : prev.isPrestationCompleted,
          }))
          console.log('Prestation suggestion received:', suggestion)
          break

        case "token":
          this.setState(prev => ({
            stream: prev.stream + event.content,
          }))
          break

        case "done":
          this.setState(prev => ({
            loading: false,
            stream: undefined,
            history: [
              ...prev.history,
              {'role': role.ASSISTANT, 'content': prev.stream},
            ]
          }))
          break
      }
    })
  }

  async handleConfirmPrestation() {
    if (!this.state.toolCall) return

    if (!this.state.prestationTemplate) {
      alert('Veuillez patienter, les données utilisateur se chargent...')
      return
    }

    const template = this.state.prestationTemplate
    if (!template.applicant || !template.applicant.uid) {
      alert('Erreur: Les données du demandeur ne sont pas définie.')
      return
    }

    if (!template.prestation_type || !template.prestation_type.uid) {
      alert('Erreur: Le type de prestation n\'est pas définie.')
      return
    }

    if (!template.address || !template.address.uid) {
      alert('Attention: L\'adresse n\'est pas définie.')
      return
    }

    this.setState({
      confirmingPrestation: true
    })

    const args = this.state.toolCall

    let prestation = await this.buildPrestationFromToolArgs(args)

    try {
      // If no specific interpreter is set but a language is specified, load default interpreters
      console.log('Before interpreter check:', {
        has_selected_interpreters: prestation.selected_interpreters && prestation.selected_interpreters.length > 0,
        selected_interpreters_count: prestation.selected_interpreters ? prestation.selected_interpreters.length : 0,
        has_languages: prestation.languages && prestation.languages.length > 0,
        languages_count: prestation.languages ? prestation.languages.length : 0,
      })

      let loadedInterpreters = []
      if ((!prestation.selected_interpreters || prestation.selected_interpreters.length === 0) &&
          prestation.languages && prestation.languages.length > 0) {
        
        console.log('Loading interpreters for language:', prestation.languages[0])
        const languageUid = prestation.languages[0].uid
        loadedInterpreters = await this.loadInterpretersByLanguage(prestation, languageUid)
        
        console.log('Loaded interpreters:', loadedInterpreters)
        if (loadedInterpreters && loadedInterpreters.length > 0) {
          prestation.selected_interpreters = loadedInterpreters.map(interp => ({ uid: interp.uid }))
          console.log('Updated selected_interpreters:', prestation.selected_interpreters)
        } else {
          console.warn('No interpreters found for language:', languageUid)
        }
      }

      console.log('template: ', args)

      if (args.beneficiary_numbers && args.beneficiary_numbers.length > 0) {
        prestation.beneficiary_numbers = args.beneficiary_numbers.map(
            ({ number, number_type, beneficiary }) => ({
            number,
            number_type,
            beneficiary,
            })
        )
      }

      prestation.consumers = []
      console.log('Final prestation payload before navigation:', prestation)

      this.props.history.push({
        pathname: '/prestation/',
        params: {
            prestation: prestation,
        }
      });
    //   this.props.history.push({pathname: '/prestation/', prestation: prestation})
    //   this.props.history.push('/prestation/', {
    //         prestation: prestation,
    //     });

    //   await prestationActions.postPrestationValidation(prestation)
      
      // Restore selected_interpreters if they were loaded but reset by validation
    //   if (loadedInterpreters && loadedInterpreters.length > 0) {
    //     prestation.selected_interpreters = loadedInterpreters.map(interp => ({ uid: interp.uid }))
    //     console.log('Restored selected_interpreters after validation:', prestation.selected_interpreters)
    //   }
      
    //   console.log('prestation payload before POST:', prestation)
    //   await prestationActions.postPrestation(prestation)
      
    //   alert('Prestation créée avec succès!')
    //   this.setState({
    //     toolCall: {},
    //     confirmingPrestation: false,
    //     message: '',
    //   })
    } catch (error) {
      console.error('Error creating prestation:', error)

    //   let errorMsg = 'Erreur lors de la création de la prestation'
    //   if (error.response && error.response.data) {
    //     const data = error.response.data
    //     if (data.field_errors) {
    //       const fieldErrors = data.field_errors
    //       const msgs = fieldErrors.map(e => `${e.field_name}: ${e.errors[0].user_message}`)
    //       errorMsg = 'Erreurs de validation:\n' + msgs.join('\n')
    //     } else if (data.user_message) {
    //       errorMsg = `Erreur du serveur: ${data.user_message}`
    //     } else {
    //       try {
    //         errorMsg = `Erreur du serveur: ${JSON.stringify(data)}`
    //       } catch (jsonErr) {
    //         errorMsg = 'Erreur serveur inconnue'
    //       }
    //     }
    //   } else if (error.message) {
    //     errorMsg = error.message
    //   }

    //   console.error('Server response data:', error.response && error.response.data)

    //   alert(errorMsg)

    //   this.setState({
    //     confirmingPrestation: false
    //   })
    }
  }

  loadInterpretersByLanguage(prestation, languageUid) {
    try {
      const startTime = prestation.ordered_date.replaceAll('.', '-') + ' ' + prestation.ordered_start_time + ':00'
      const endTime = prestation.ordered_date.replaceAll('.', '-') + ' ' + prestation.ordered_end_time + ':00'
      const prestationTypeCode = (prestation.prestation_type && prestation.prestation_type.code) ||
                                  (prestation.prestation_type && prestation.prestation_type.uid)
      const user = this.props.user && this.props.user.uid

      const query = {
        languages: languageUid,
        start_time: startTime,
        end_time: endTime,
        prestation_type: 1,
        user: user,
        objects_count: false,
        meta: false,
      }

      console.log('Fetching interpreters with query:', query)

      return prestationActions.loadInterpreters(query)
        .then((result) => {
          const interpreters = (result.data && result.data.objects && result.data.objects.objects) ||
            (result.data && result.data.object) ||
            []

          if (interpreters.length > 0) {
            return interpreters
          }
          return []
        })
        .catch((error) => {
          console.error('Error loading interpreters by language:', error)
          return []
        })
    } catch (error) {
      console.error('Error preparing interpreter search:', error)
      return Promise.resolve([])
    }
  }

  async buildPrestationFromToolArgs(args) {
    const template = this.state.prestationTemplate || {}
    let new_date = null
    if (args.date) {
      const [day, month, year] = args.date.split('.')
      new_date = `${year}.${month}.${day}`
    }
    // let ordered_start_time = this.parseDateTime(new_date, args.start)
    // let ordered_end_time = this.parseDateTime(new_date, args.end)
    // let ordered_start_time = new_date + ' ' + args.start
    // let ordered_end_time = new_date + ' ' + args.end

    let prestation = {
      ...template,
      ordered_date: new_date,
      ordered_start_time: args.start,
      ordered_end_time: args.end,
      selected_interpreters: template.selected_interpreters || [],
    }

    if (args.language) {
      const langValue = typeof args.language === 'object' ? args.language.value : args.language
      const langUid = typeof args.language === 'object' ? args.language.uid : args.language
      
      if (template.languages && template.languages.length > 0) {
        const matchingLanguage = template.languages.find(l => 
          l.value === langValue || l.uid === langUid || l.value === args.language
        )
        if (matchingLanguage) {
          prestation.languages = [matchingLanguage]
        } else {
          prestation.languages = [template.languages[0]]
        }
      } else {
        prestation.languages = [typeof args.language === 'object' ? args.language : { uid: args.language, value: args.language }]
      }
    }

    if (args.address) {
      prestation.address = args.address
    }

    if (args.preferred_sex) {
        const selectedSex = this.props.sexes.find((sex) => sex.value === args.preferred_sex)
        prestation.preferred_sex = selectedSex || null
    }

    if (args.preferred_nationality_origin && prestation.languages && prestation.languages.length > 0) {
        prestation.preferred_nationality_origin = args.preferred_nationality_origin

    //     let origins = []
        
    //     prestation.languages[0].forEach((language) => {
    //         const origin = language.nationalities.find((origin) => {
    //         return origin.nationality.toUpperCase().startsWith(searchText.toUpperCase())
    //         })
    
    //         if (origin) {
    //         origins.push({
    //             type: constants.COUPLE_NATIONATLITY_LANGUAGE_TYPE,
    //             language_uid: language.uid,
    //             origine_uid: origin.uid,
    //             value: language.value + ', ' + origin.nationality
    //         })
    //         }
    //     })
    //   prestation.preferred_nationality_origin = { nationality: args.preferred_nationality_origin }
    }

    if (args.preferred_qualification) {
      const qual = typeof args.preferred_qualification === 'object'
        ? args.preferred_qualification
        : { uid: args.preferred_qualification }
      prestation.preferred_qualifications = [qual]
    }

    if (args.preferred_skill) {
      const skill = typeof args.preferred_skill === 'object'
        ? args.preferred_skill
        : { uid: args.preferred_skill }
      prestation.preferred_skills = [skill]
    }

    if (args.interpreter) {
      const intUid = typeof args.interpreter === 'object' ? args.interpreter.uid : args.interpreter
      if (intUid) {
        prestation.selected_interpreters = [{ uid: intUid }]
      }
    }

    if (args.beneficiary_numbers) {
      const validBN = args.beneficiary_numbers.filter(bn => bn && (bn.number || bn.beneficiary))
      if (validBN.length > 0) {
        prestation.beneficiary_numbers = validBN
      }
    }

    if (args.consumers) {
      const consArray = Array.isArray(args.consumers) ? args.consumers : [args.consumers]
      const validCons = consArray.filter(cons => {
        const consStr = String(cons).trim()
        return consStr.length > 0
      })
      if (validCons.length > 0) {
        prestation.consumers = validCons.map(cons => 
          typeof cons === 'object' ? cons : { uid: cons }
        )
      }
    }

    if (args.paying_customer) {
      const pcValue = Array.isArray(args.paying_customer) ? args.paying_customer[0] : args.paying_customer
      prestation.paying_customer = typeof pcValue === 'object' ? pcValue : { uid: pcValue }
    }

    if (args.applicant && args.applicant.uid) {
        const applicant_obj = await applicantActions.getApplicant(args.applicant.uid)
        prestation.applicant = applicant_obj
    }

    return prestation
  }

  parseDateTime(dateStr, timeStr) {
    try {
      if (!dateStr || !timeStr) {
        return new Date().toISOString()
      }
      let timeNorm = String(timeStr).trim().toLowerCase()
      timeNorm = timeNorm.replace(/h/, ':')
      // remove spaces
      timeNorm = timeNorm.replace(/\s+/g, '')
      const hhmm = timeNorm.match(/^(\d{2})(\d{2})$/)
      if (hhmm) timeNorm = `${hhmm[1]}:${hhmm[2]}`
      if (/^\d{1,2}:\d{2}$/.test(timeNorm)) {
        timeNorm = `${timeNorm}:00`
      }
      timeNorm = timeNorm.replace(/[^0-9:]/g, '')

      const combined = `${dateStr} ${timeNorm}`

      let m = moment(combined, ['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD HH:mm'], true)
      if (!m.isValid()) {
        m = moment(combined)
      }

      if (m.isValid()) {
        return m.format('YYYY-MM-DDTHH:mm:ssZ')
      }
      return combined.replace(' ', 'T')
    } catch (e) {
      console.error('Error parsing datetime:', e)
      return new Date().toISOString()
    }
  }

  getFieldValue(value) {
    if (!value) return this.props.intl.formatMessage(messages.SUMMARY_NOT_SPECIFIED)
    
    if (Array.isArray(value)) {
      const displayValues = value
        .map(item => this.getFieldValue(item))
        .filter(item => item && item !== this.props.intl.formatMessage(messages.SUMMARY_NOT_SPECIFIED))

      if (displayValues.length > 0) {
        return displayValues.join(', ')
      }

      return this.props.intl.formatMessage(messages.SUMMARY_NOT_SPECIFIED)
    }

    switch (typeof value) {
    case 'string':
    case 'number':
      return String(value)

    case 'object':
      if (value.beneficiary_name) return String(value.beneficiary_name)
      if (value.name) return String(value.name)
      if (value.label) return String(value.label)
      if (value.display_name) return String(value.display_name)
      if (value.value) return String(value.value)
      if (value.address) return String(value.address)
      if (value.uid) return String(value.uid)

      try {
        return JSON.stringify(value)
      } catch (e) {
        return this.props.intl.formatMessage(messages.SUMMARY_NOT_VALID)
      }

    default:
      return this.props.intl.formatMessage(messages.SUMMARY_NOT_SPECIFIED)
    }
  }

  getDisplayedValue(field, value) {
    if (this.state.invalidFields && this.state.invalidFields.includes(field)) {
      return this.props.intl.formatMessage(messages.SUMMARY_NOT_VALID)
    }
    return this.getFieldValue(value)
  }

  renderToolResult() {
    if (!this.state.toolCall || !this.state.isPrestationCompleted) {
      return (
        <Card title={this.props.intl.formatMessage(messages.SUMMARY_TITLE)}>
          <div style={styles.emptyToolResult}>
            <Typography style={{color: 'grey'}}>
              {this.props.intl.formatMessage(messages.SUMMARY_PLACEHOLDER)}
            </Typography>
          </div>
        </Card>
      )
    }

    const args = this.state.toolCall
    const hasOptionalDetails = optionalFields.some(
      key => args[key] !== undefined && args[key] !== null
    )

    return (
      <Card title={this.props.intl.formatMessage(messages.SUMMARY_TITLE)}>
        <div style={styles.toolResult}>
          <div style={styles.toolFields}>
            <div style={styles.fieldRow}>
              <div style={styles.fieldColumn}>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>{this.props.intl.formatMessage(messages.SUMMARY_DATE)}</label>
                  <div style={styles.value}>{this.getDisplayedValue('date', args.date)}</div>
                </div>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>{this.props.intl.formatMessage(messages.SUMMARY_START_TIME)}</label>
                  <div style={styles.value}>{this.getDisplayedValue('start', args.start)}</div>
                </div>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>{this.props.intl.formatMessage(messages.SUMMARY_END_TIME)}</label>
                  <div style={styles.value}>{this.getDisplayedValue('end', args.end)}</div>
                </div>
              </div>
              <div style={styles.fieldColumn}>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>{this.props.intl.formatMessage(messages.SUMMARY_LANGUAGE)}</label>
                  <div style={styles.value}>{this.getDisplayedValue('language', args.language)}</div>
                </div>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>{this.props.intl.formatMessage(messages.SUMMARY_ADDRESS)}</label>
                  <div style={styles.value}>{this.getDisplayedValue('address', args.address)}</div>
                </div>
              </div>
            </div>

            {hasOptionalDetails && (
              <div>
                {args.applicant && (
                  <div style={styles.smallField}>
                    <span style={styles.smallLabel}>{this.props.intl.formatMessage(messages.SUMMARY_APPLICANT)}</span> {this.getFieldValue(args.applicant)}
                  </div>
                )}
                {args.beneficiary_name && (
                  <div style={styles.smallField}>
                    <span style={styles.smallLabel}>{this.props.intl.formatMessage(messages.SUMMARY_BENEFICIARY_NAME)}</span> {this.getFieldValue(args.beneficiary_name)}
                  </div>
                )}
                {args.beneficiary_number && (
                  <div style={styles.smallField}>
                    <span style={styles.smallLabel}>{this.props.intl.formatMessage(messages.SUMMARY_BENEFICIARY_NUMBER)}</span> {this.getFieldValue(args.beneficiary_number)}
                  </div>
                )}
                {args.beneficiary_type && (
                  <div style={styles.smallField}>
                    <span style={styles.smallLabel}>{this.props.intl.formatMessage(messages.SUMMARY_BENEFICIARY_TYPE)}</span> {this.getFieldValue(args.beneficiary_type)}
                  </div>
                )}
                {args.interpreter && (
                  <div style={styles.smallField}>
                    <span style={styles.smallLabel}>{this.props.intl.formatMessage(messages.SUMMARY_INTERPRETER)}</span> {this.getFieldValue(args.interpreter)}
                  </div>
                )}
                {args.preferred_sex && (
                  <div style={styles.smallField}>
                    <span style={styles.smallLabel}>{this.props.intl.formatMessage(messages.SUMMARY_PREFERRED_SEX)}</span> {this.getFieldValue(args.preferred_sex)}
                  </div>
                )}
                {args.preferred_nationality_origin && (
                  <div style={styles.smallField}>
                    <span style={styles.smallLabel}>{this.props.intl.formatMessage(messages.SUMMARY_PREFERRED_NATIONALITY_ORIGIN)}</span> {this.getFieldValue(args.preferred_nationality_origin.nationality)}
                  </div>
                )}
                {args.preferred_qualification && (
                  <div style={styles.smallField}>
                    <span style={styles.smallLabel}>{this.props.intl.formatMessage(messages.SUMMARY_PREFERRED_QUALIFICATIONS)}</span> {this.getFieldValue(args.preferred_qualification)}
                  </div>
                )}
                {args.preferred_skill && (
                  <div style={styles.smallField}>
                    <span style={styles.smallLabel}>{this.props.intl.formatMessage(messages.SUMMARY_PREFERRED_SKILLS)}</span> {this.getFieldValue(args.preferred_skill)}
                  </div>
                )}
              </div>
            )}
          </div>

          <div style={styles.toolActions}>
            <RaisedButton
              label={this.props.intl.formatMessage(messages.VALIDATE_BUTTON)}
              primary={true}
              onClick={this.handleConfirmPrestation}
              disabled={this.state.confirmingPrestation || !this.state.isPrestationValid}
              style={styles.confirmButton}
            />
            {this.state.confirmingPrestation && (
              <CircularProgress
                size={30}
                style={{marginLeft: 10}}
              />
            )}
          </div>
        </div>
      </Card>
    )
  }

  render () {
    const identityName = (this.props.user && this.props.user.identityName) || Cookies.get('identityName') || '?'
    const userInitial = identityName.charAt(0)

    const displayMessages = this.state.history.filter(text => {
      if (text.role === role.SYSTEM) return false
      if (text.content && text.content.trim().startsWith('{') && text.content.length > 100) return false
      return true
    })

    if (this.state.stream !== undefined && !this.state.loadingDialog) {
      displayMessages.push({'role': role.ASSISTANT, 'content': this.state.stream})
    }

    return (
      <Base
        page={5}
      >
        <FixedPanel ratio='2:1'>
          <Panel>
            <Card title={
                <div>
                    {/* this.props.intl.formatMessage(messages.CHATBOT_HEADER) */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                        }}>
                        <span>chatin</span>
                        
                              <IconButton
                                tooltip={''}
                                style={{margin: 0, padding: 0, height: 23}}

                                onClick={() => this.handleRestartChatbot()}
                              >
                                <FontIcon
                                  className='material-icons inline middle'
                                >
                                  refresh
                                </FontIcon>
                              </IconButton>
                        </div>
                </div>
                }>
              <div style={styles.history} ref={this.historyRef}>
                <List>
                  {
                    displayMessages.map((text, key) => (
                      <ListItem
                        key={key}
                        disablePadding
                        style={{
                          ...styles.historyItem,
                          backgroundColor: text.role === role.ASSISTANT? 'whitesmoke':'white'
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar>
                            {
                              text.role === role.ASSISTANT
                              ? <Face />
                              : userInitial
                            }
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText primary={
                          <ReactMarkdown>{text.content}</ReactMarkdown>
                        } />
                      </ListItem>
                    ))
                  }
                  {
                    this.state.loadingDialog
                    ? <ListItem
                      disablePadding
                      style={{backgroundColor: 'whitesmoke'}}
                    >
                      <ListItemAvatar>
                        <Avatar>
                          <Face />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText primary={
                        <p>
                          <CircularProgress
                            size={30}
                          />
                        </p>
                      } />
                    </ListItem>
                    : undefined
                  }
                  <div ref={this.bottomRef} />
                </List>
              </div>
              <div style={styles.textSection}>
                <div style={styles.textFieldWrapper}>
                  <TextField
                    inputRef={this.typeRef}
                    name='message'
                    multiLine={true}
                    rows={2}
                    value={this.state.message}
                    onChange={(event, value) => this.setState({message: value})}
                    placeholder={this.props.intl.formatMessage(messages.PROMPT_PLACEHOLDER)}
                    floatingLabelText={this.props.intl.formatMessage(messages.CHAT_FIELD_LABEL)}
                    onKeyDown={(e) => {
                      if (!e.shift && e.key === key.ENTER) {
                        e.preventDefault()
                        this.handleOnClick()
                      }
                    }}
                  />
                </div>

                <RaisedButton
                  label={this.props.intl.formatMessage(messages.PROMPT_SEND_BUTTON)}
                  primary={true}
                  onClick={this.handleOnClick}
                  disabled={this.state.loading}
                  style={{...
                    styles.button,
                    flex: '0 0 auto'
                  }}
                />

              </div>
            </Card>
          </Panel>
          <Panel>
            {this.renderToolResult()}
          </Panel>
        </FixedPanel>
      </Base>
    )
  }
}

const styles = {
  history: {
    minHeight: '200px',
    maxHeight: '60vh',
    overflowY: 'auto',
  },
  historyItem: {
    minHeight: '85px'
  },
  textSection: {
    display: 'flex',
    flexDirection: 'row',
  },
  textFieldWrapper: {
    flex: '1',
  },
  button: {
    minWidth: 200,
    marginTop: 30,
    height: 40
  },
  toolResult: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    padding: '20px',
  },
  emptyToolResult: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '200px',
  },
  toolFields: {
    flex: 1,
    overflowY: 'auto',
    marginBottom: '20px',
  },
  fieldRow: {
    display: 'flex',
    gap: '20px',
    marginBottom: '15px',
  },
  fieldColumn: {
    flex: 1,
  },
  fieldGroup: {
    marginBottom: '15px',
    paddingBottom: '10px',
    borderBottom: '1px solid #eee',
  },
  label: {
    fontWeight: 'bold',
    color: '#666',
    display: 'block',
    marginBottom: '5px',
    fontSize: '13px',
  },
  value: {
    color: '#333',
    fontSize: '14px',
    fontWeight: '500',
  },
  optionalFields: {
    marginTop: '15px',
    padding: '10px',
    backgroundColor: '#f5f5f5',
    borderRadius: '4px',
    borderLeft: '3px solid #ccc',
  },
  smallField: {
    fontSize: '12px',
    marginBottom: '8px',
    display: 'flex',
    gap: '8px',
  },
  smallLabel: {
    fontWeight: 'bold',
    color: '#666',
    minWidth: '80px',
  },
  toolActions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: '15px',
    borderTop: '2px solid #eee',
  },
  confirmButton: {
    minWidth: 180,
    height: 40,
    fontSize: '14px',
  }
}

const mapStateToProps = (state) => ({
  user: state.User.user,
  sexes: state.ApplicantPrestation.sexes,
  qualifications: state.ApplicantPrestation.qualifications,
  skills: state.ApplicantPrestation.skills,
})

// export default connect(mapStateToProps, undefined)(injectIntl(PrestationCriterias))

export default connect(mapStateToProps, undefined)(injectIntl(Chatbot))


// WEBPACK FOOTER //
// ./src/components/pages/Chatbot.js